import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { API_URL } from '../constants';
import { SessionStorageKey } from '../enums/SessionStorageKey';
import { cleanLoginData } from './cleanLoginData';
import { storeLoginRedirectPath } from './storeLoginRedirectPath';
import { RoutePath } from '../enums/RoutePath';

export const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_URL,
});

axiosInstance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = sessionStorage.getItem(SessionStorageKey.Token);

  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }

  return config;
});

axiosInstance.interceptors.response.use(undefined, async (error: AxiosError): Promise<any> => {
  if (error.response?.status !== 401) {
    return Promise.reject(error);
  }

  // Already at the login screen, which makes its own requests. Sending it back to itself
  // on a rejected sign-in would reload the page out from under the form.
  if (window.location.pathname === RoutePath.Login) {
    return Promise.reject(error);
  }

  storeLoginRedirectPath();
  cleanLoginData();

  // A real navigation, not react-router's `redirect`.
  //
  // `redirect` builds a Response for a data router to act on when a loader or action
  // returns it. Called here the value was discarded and nothing happened — and this app
  // mounts BrowserRouter, which has no loaders to return it to in the first place. So the
  // session ended, the token was cleared, and the page stayed exactly where it was: still
  // rendering the dashboard, still polling notifications every thirty seconds and eight
  // dashboard endpoints every minute, every one of them now unauthenticated, for as long
  // as the tab stayed open.
  //
  // Leaving the page is also the only thing that stops those timers, which live in
  // component state this interceptor cannot reach.
  window.location.assign(RoutePath.Login);

  return Promise.reject(error);
});
